import React, { useEffect } from 'react';
import {
  withStyles,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import NavigateNext from '@material-ui/icons/NavigateNext';
import NavigateBefore from '@material-ui/icons/NavigateBefore';
import Paper from '@material-ui/core/Paper';
import { Translation } from 'react-i18next';
import { bool, string, func, object, any } from 'prop-types';
import LinearProgress from '@material-ui/core/LinearProgress';
import DeclarationOfApplicant from '../../forms/Application/DeclarationOfApplicant/';
import ErrorMsg from '../components/ErrorMsg';
import Footer from '../components/Footer';

const styles = theme => ({
  rootContainer: {
    marginTop: theme.spacing(10),
  },
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  demo: {
    height: 240,
  },
  paper: {
    padding: theme.spacing(2),
    height: '100%',
    color: theme.palette.text.secondary,
  },
  control: {
    padding: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonLeft: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(8),
  },
  buttonRight: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#357a38',
    color: '#ffffff',
  },
  buttonBack: {
    backgroundColor: '#b22a00',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  buttonRightSingle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  buttonLeftContainer: {
    display: 'flex',
  },
  buttonRightContainer: {
    justifyContent: 'flex-end',
    flexDirection: 'row',
    display: 'flex',
  },
  progress: {
    flexGrow: 1,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  saveButton: {
    background: '#0280CA',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#6fbf73',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',
      contrastText: '#ffffff',
    },
  },
  typography: {
    useNextVariants: true,
  },
});

function DeclarationPage(props) {
  const {
    loadFees,
    visatypeId,
    paymentRecord,
    checkZeroPayment,
    submitPaymentRequest,
    classes,
    isFetching,
    hasDeclared,
    hasReadTerms,
    declarationChanged,
    readTermsChanged,
    error,
    amount,
    application,
    submit,
    history,
    canProceed,
    isLAO,
    zeroPayment,
    submitZeroPayment,
    uuid,
  } = props;

  useEffect(() => {
    if (application.status === 'COMPLETED') {
      checkZeroPayment(application.uuid);
    }
  }, [application.uuid])

  useEffect(() => {
    if ((!amount || amount === 0) && !isLAO) loadFees(visatypeId);
    if (paymentRecord) {
      submitPaymentRequest(paymentRecord.uuid, 'APPROVED');
      history.push(`/payment/${uuid}/success`);
    }
  }, [
    visatypeId,
    paymentRecord,
    submitPaymentRequest,
    amount,
    application.status,
    application.uuid,
  ]);

  const onNext = () => {
    history.push(`/payment/${uuid}`);
  };

  {
    const {} = props;
    return (
      <Translation>
        {t => {
          return (
            <div className={classes.rootContainer}>
              <Grid
                container
                spacing={1}
                alignItems="center"
                direction="row"
                justifyContent="center"
              >
                <Grid item xs={11}>
                  <Typography variant="h4" align="center" gutterBottom>
                    {t('declarationAndPayment')}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <Paper className={classes.root} elevation={3}>
                    {!!error && <ErrorMsg error={error} />}
                    {isFetching && amount > 0 && (
                      <LinearProgress className={classes.progress} />
                    )}
                    <DeclarationOfApplicant
                      hasDeclared={hasDeclared}
                      hasReadTerms={hasReadTerms}
                      declarationChanged={declarationChanged}
                      readTermsChanged={readTermsChanged}
                    />

                    {(application.status === 'IN_CREATION' ||
                      application.status === 'CANCELED') && (
                      <div className={classes.buttonWrapper}>
                        <MuiThemeProvider theme={theme}>
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={isFetching || !canProceed}
                            className={classes.buttonRight}
                            onClick={() => {
                              submit(application.uuid, history);
                            }}
                          >
                            {t('continue')}
                            <NavigateNext className={classes.rightIcon} />
                          </Button>
                        </MuiThemeProvider>
                      </div>
                    )}
                  </Paper>
                </Grid>
                <Grid container direction="row" justifyContent="space-around">
                  <Grid item xs={5} className={classes.buttonLeftContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.saveButton}
                      disabled={isFetching}
                      component={Link}
                      to={'/applications'}
                    >
                      {t('backTo')}
                    </Button>
                  </Grid>
                  <Grid item xs={5} className={classes.buttonRightContainer}>
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.buttonBack}
                      disabled={isFetching}
                      component={Link}
                      to={'/view'}
                    >
                      <NavigateBefore className={classes.leftIcon} />
                      {t('back')}
                    </Button>
                    <MuiThemeProvider theme={theme}>
                      {application.status === 'COMPLETED' && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            zeroPayment && zeroPayment.isTotalPaymentCurZero
                              ? submitZeroPayment(
                                  zeroPayment.quoteId,
                                  history,
                                  uuid
                                )
                              : onNext();
                          }}
                          className={classes.buttonRight}
                          disabled={isFetching || !canProceed}
                        >
                          {t('proceedTo')}
                          <NavigateNext className={classes.rightIcon} />
                        </Button>
                      )}
                    </MuiThemeProvider>
                  </Grid>
                </Grid>
              </Grid>
              <Footer />
            </div>
          );
        }}
      </Translation>
    );
  }
}

DeclarationPage.propTypes = {
  classes: object.isRequired,
  isFetching: bool.isRequired,
  error: string,
  hasDeclared: bool,
  declarationChanged: func.isRequired,
  hasReadTerms: bool,
  readTermsChanged: func.isRequired,
  amount: any,
  currency: string,
  loadFees: func.isRequired,
  canProceed: bool.isRequired,
  fee: object,
  getQuote: func.isRequired,
};

export default withStyles(styles)(DeclarationPage);



// WEBPACK FOOTER //
// ./src/view/scenes/Declaration/DeclarationPage.js